import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { cn } from '../cn';

/**
 * Button — shadcn/ui pattern (Radix Slot + class-variance-authority),
 * adapted to WorkingClock's tokens (DESIGN.md §5 "Buttons").
 *
 * Shape is `rounded-sm` (6px) for every variant except `timer`, the one
 * place the system allows a pill-shaped, larger-than-default control (the
 * start/stop timer button — DESIGN.md's signature component).
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-sm text-label font-medium',
    'transition duration-150 motion-reduce:transition-none',
    // Tactile press feedback — confirms the click without "performing" (DESIGN.md §6).
    'motion-safe:active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-gold text-ink hover:bg-gold-deep hover:text-bg active:bg-gold-deep active:text-bg',
        secondary: 'border border-border bg-transparent text-ink hover:bg-surface active:bg-surface',
        ghost: 'bg-transparent text-ink hover:bg-surface active:bg-surface',
        danger: 'bg-danger text-white hover:bg-danger/90 active:bg-danger/90',
        link: 'bg-transparent p-0 text-gold underline-offset-4 hover:underline',
        /** The one pill-shaped, oversized control in the whole system — the timer start/stop button. */
        timer:
          'rounded-full bg-gold px-8 text-title font-semibold text-ink shadow-none hover:bg-gold-deep hover:text-bg active:bg-gold-deep active:text-bg',
      },
      size: {
        default: 'h-9 px-4 [&_svg]:size-4',
        sm: 'h-8 px-3 [&_svg]:size-4',
        lg: 'h-11 px-6 [&_svg]:size-5',
        icon: 'h-9 w-9 [&_svg]:size-4',
        timer: 'h-14 [&_svg]:size-6',
      },
    },
    defaultVariants: { variant: 'primary', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner in place of the leading icon and disables the button. */
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, isLoading = false, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {isLoading ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
